/* eslint-disable react/forbid-prop-types, react/no-unstable-nested-components */
import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment';
import ReplayIcon from '@mui/icons-material/Replay';
import { Grid, Card, CardContent, CardHeader } from '@mui/material';
import { PublisherNode } from 'relevant-shared/objects/pubTree';
import { allObjects } from 'relevant-shared/objects/objectStore';
import { ensureLoaded } from '../../lib/objectStoreUtils';

import ExpandSelector from '../ExpandSelector';
import OperationWrapper from '../OperationWrapper';
import classes from '../../api/classes';
import DataGrid from '../DataGrid';
import ReactUtils from '../../lib/reactUtils';
import JobButton from '../JobButton';
import { SimpleAlert } from '../Wrappers';
import PopupSelector from '../PopupSelector';
import { maybeToReadable, modelDesc, featureDesc, featureName, TreeWrapper, TreeGraph, codeToPlacMap } from './utils';
import Switch from '../Switch';
import CallDebugger from './callDebugger';

const { BoosterModel } = classes;

const DEFAULT_GRID_PROPS = {
	disableColumnMenu: true,
	disableSelectionOnClick: true,
	disableColumnReorder: true,
	hideFooter: true,
};

class BoosterStatus extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			useCollectionValMap: false,
		};
		this.fld = ReactUtils.fld(this);
	}

	getParams() {
		const { models } = this.state;
		const { paramFilter } = this.props;
		return _.sortBy(_.filter(models.BoosterParamState, paramFilter), 'fieldDescription');
	}

	of(type, id) {
		const { models } = this.state;
		return _.find(models[type], { id });
	}

	async loadStatus() {
		const CLASSES = ['BoosterParamState', 'BoosterModelGroup', 'BoosterModel'];
		await ensureLoaded();
		this.setState({
			constants: BoosterModel.getConstants(),
			models: Object.fromEntries(await Promise.all(CLASSES.map((cls) => (async () => (
				[cls, await classes[cls].list()]
			))()))),
		});
	}

	renderFeature(feature) {
		const { type, binLimits, vals } = feature;
		const keys = [
			'featureIdx',
			'type',
			...(type === 'calc'
				? ['calcIndex', 'calcType', 'divideByNrBack', 'metric', 'msRange', 'slotsBackJump']
				: []
			),
			...(feature.name ? ['name'] : []),
		];
		return (
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<table>
						<tbody>
							{keys.map((key) => (
								<tr key={key}>
									<td style={{ paddingRight: 15 }}><i>{key}: </i></td>
									<td>{feature[key]}</td>
								</tr>
							))}
						</tbody>
					</table>
				</Grid>
				{binLimits?.length && (
					<Grid item xs={12}>
						<DataGrid
							{...DEFAULT_GRID_PROPS}
							identifier={({ idx }) => idx}
							definitions={[
								{
									key: 'idx',
									title: 'Bin index',
								},
								{
									key: 'limit',
									title: 'Bin limit (<)',
								},
							]}
							data={binLimits.map((limit, idx) => ({ limit, idx }))}
						/>
					</Grid>
				)}
				{vals?.length && (
					<Grid item xs={12}>
						<DataGrid
							{...DEFAULT_GRID_PROPS}
							identifier={({ name }) => name}
							definitions={[
								{
									key: 'idx',
									title: 'Index',
									width: 70,
								},
								{
									key: 'name',
									title: 'Name',
									transform: (name) => maybeToReadable(name, feature),
								},
								{
									key: 'count',
									title: 'Samples',
									width: 100,
								},
							]}
							data={vals.map(([name, count], idx) => ({ name, count, idx }))}
						/>
					</Grid>
				)}
			</Grid>
		);
	}

	renderValueOrder(order, feature, isBinLimits) {
		// { orgIdx, orderIdx, name, count }
		return (
			<DataGrid
				{...DEFAULT_GRID_PROPS}
				identifier={({ orderIdx }) => orderIdx}
				definitions={[
					{
						key: 'orderIdx',
						title: 'Order index',
						width: 100,
					},
					{
						key: 'name',
						title: isBinLimits ? 'Bin limit (<)' : 'Value',
						transform: (name) => maybeToReadable(name, feature),
					},
				]}
				data={order}
			/>
		);
	}

	renderTree(tree) {
		const { featureValMapping } = tree;
		return (
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<Switch
						style={{ flexGrow: 0.5 }}
						label="Use value mapping from bin collection during training"
						{...this.fld('useCollectionValMap')}
					/>
				</Grid>
				<Grid item xs={12}>
					<ExpandSelector
						title="Value mappings"
						selected={featureValMapping}
						autoExpandKey="features"
					>
						<DataGrid
							{...DEFAULT_GRID_PROPS}
							identifier={({ feature }) => feature.id}
							definitions={[
								{
									key: 'feature',
									title: 'Feature',
									format: (feature) => (
										<PopupSelector
											size="lg"
											title={featureDesc(feature)}
											content={() => this.renderFeature(feature)}
										/>
									),
								},
								{
									key: 'order',
									title: 'Value order',
									format: (order, { feature }) => (
										<PopupSelector
											title="[View order]"
											size="md"
											content={() => this.renderValueOrder(order, feature, false)}
										/>
									),
								},
								{
									key: 'binLimits',
									title: 'Value bin limits',
									format: (binLimits, { feature }) => (
										<PopupSelector
											title="[View bin limits]"
											size="md"
											content={() => this.renderValueOrder(binLimits, feature, true)}
										/>
									),
								},
							]}
							data={featureValMapping}
						/>
					</ExpandSelector>
				</Grid>
				<Grid item xs={12}>
					<TreeGraph tree={tree} />
				</Grid>
			</Grid>
		);
	}

	renderRandLimits({ randLimits, placMap, nodesById }) {
		const rows = _.entries(randLimits).map(([id, limit]) => {
			const obj = placMap[id];
			let settings;
			const node = obj && nodesById[obj.id];
			if (node) {
				const { parents, type } = node;
				settings = {
					style: {
						marginLeft: `${parents.length * 20}px`,
						fontWeight: type === 'PlacementNode' ? 'normal' : 'bold',
					},
					sortStr: [node, ...node.parents]
						.reverse()
						.map((n) => [n.name.toLowerCase(), n.id])
						.flat()
						.join('||'),
					name: node.name,

				};
			} else { // Unknown id
				settings = { orderArr: [`'\uFFFF'${id}`], style: { color: 'red' }, name: id };
			}
			return { id, limit, ...settings };
		});
		return (
			<DataGrid
				{...DEFAULT_GRID_PROPS}
				identifier={({ id }) => id}
				definitions={[
					{
						key: 'name',
						title: 'Unit',
						format: (name, { style }) => <div style={style}>{name}</div>,
					},
					{
						key: 'limit',
						title: 'Upper limit',
					},
				]}
				data={_.sortBy(rows, 'sortStr')}
			/>
		);
	}

	renderModel(model, treeList) {
		const { useCollectionValMap } = this.state;
		const { features, randLimits } = model;
		const trees = _.sortBy(treeList, 'treeIdx').map((tree, __, arr) => new TreeWrapper({
			tree,
			model,
			trees: arr,
			useCollectionValMap,
		}));
		const splitUsage = _.groupBy(_.filter(_.map(trees.flatMap((t) => t.nodes), 'feature')), 'id');
		const statGetter = (path) => (__, tree) => {
			if (tree.treeIdx >= trees.length - 1) {
				return '';
			}
			const val = _.get(trees[tree.treeIdx + 1], path);
			return typeof val === 'number' ? val.toFixed(3) : '';
		};
		return (
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<ExpandSelector
						title="Features"
						selected={features}
						autoExpandKey="features"
					>
						<DataGrid
							{...DEFAULT_GRID_PROPS}
							identifier={({ id }) => id}
							definitions={[
								{
									key: 'featureIdx',
									title: 'Feature index',
									format: (idx, feature) => (
										<>
											<span>{idx}</span>&nbsp;
											<PopupSelector
												size="lg"
												title={['featureIdx', 'type', 'name']
													.map((f) => feature[f])
													.filter((v) => ![null, undefined, ''].includes(v))
													.join(' - ')}
												linkTitle="[View]"
												autoExpandKey={`feature_${idx}`}
												content={() => this.renderFeature(feature)}
											/>
										</>
									),
								},
								{
									key: 'type',
									title: 'Type',
								},
								{
									key: 'name',
									title: 'Name',
									transform: (__, feature) => featureName(feature),
								},
								{
									key: 'id',
									title: 'Splits',
									width: 80,
									transform: (id) => splitUsage[id]?.length || 0,
								},
							]}
							data={_.sortBy(features, 'featureIdx')}
						/>
					</ExpandSelector>
				</Grid>
				<Grid item xs={12}>
					<ExpandSelector
						title="Trees"
						selected={trees}
						autoExpandKey="trees"
					>
						<DataGrid
							{...DEFAULT_GRID_PROPS}
							identifier={({ id }) => id}
							definitions={[
								{
									key: 'treeIdx',
									title: 'Tree index',
									format: (idx, tree) => (
										<>
											<span>{idx}</span>&nbsp;
											<PopupSelector
												size="lg"
												title={`${modelDesc(model)} >> Tree ${idx}`}
												linkTitle="[View]"
												autoExpandKey={`tree_${idx}`}
												content={() => this.renderTree(tree)}
											/>
										</>
									),
								},
								{
									key: 'treeNodes',
									title: 'Nodes',
									transform: (nodes) => nodes.length,
								},
								{
									key: 'medianTrainErr',
									title: 'Med. Train Err.',
									transform: statGetter('residualStats.medianAbsErr'),
								},
								{
									key: 'medianTrainErr',
									title: 'Med. Validate Err.',
									transform: statGetter('validateResidualStats.medianAbsErr'),
								},
								{
									key: 'auc',
									title: 'AUC',
									transform: statGetter('validateResidualStats.auc'),
								},
							]}
							data={trees}
						/>
					</ExpandSelector>
				</Grid>
				<Grid item xs={12}>
					<ExpandSelector
						title="Random limits for floor prices"
						autoExpandKey="randLimits"
						fn={() => codeToPlacMap(_.keys(randLimits))}
						content={(__, ___, placMap) => this.renderRandLimits({
							randLimits,
							placMap,
							nodesById: Object.assign({}, ...allObjects('Publisher').map((pub) => (
								new PublisherNode(pub).byId
							))),
						})}
					/>
				</Grid>
			</Grid>
		);
	}

	renderModelList(models) {
		return (
			<DataGrid
				{...DEFAULT_GRID_PROPS}
				identifier={({ id }) => id}
				definitions={[
					{
						key: 'modelNr',
						title: 'Sequence number',
						format: (nr, model) => (
							<>
								<span>{nr}</span>&nbsp;
								<PopupSelector
									size="lg"
									title={modelDesc(model)}
									linkTitle="[View]"
									autoExpandKey={`model_${model.modelNr}`}
									fn={() => model.getTrees()}
									content={(__, trees) => this.renderModel(model, trees)}
								/>
							</>
						),
					},
					{
						key: 'label',
						title: 'Type',
					},
					{
						key: 'status',
						title: 'Status',
					},
					{
						key: 'trainDataEnd',
						title: 'Data end',
						format: (d) => moment.utc(d).format('YYYY-MM-DD HH:mm'),
					},
				]}
				data={_.sortBy(models, (m) => -m.modelNr)}
			/>
		);
	}

	renderModelGroup(group) {
		const { modelIds } = group;
		return this.renderModelList(modelIds.map((id) => this.of('BoosterModel', id)));
	}

	renderAvailGroup(avail) {
		const { modelGroupId, percentage } = avail;
		return (
			<Card>
				<CardHeader title={`${percentage.toFixed(1)} %`} />
				<CardContent>
					{this.renderModelGroup(this.of('BoosterModelGroup', modelGroupId))}
				</CardContent>
			</Card>
		);
	}

	renderParam(param) {
		const { fieldDescription, availModelGroups } = param;
		const byPerc = _.sortBy(availModelGroups, (a) => -a.percentage);
		return (
			<ExpandSelector
				title={fieldDescription}
				expanded={this.getParams().length === 1}
			>
				<Grid container spacing={3}>
					{!byPerc.length
						? (
							<Grid item xs={12}>
								No model group created
							</Grid>
						) : byPerc.map((avail) => (
							<Grid item xs={12} key={avail.modelGroupId}>
								{this.renderAvailGroup(avail)}
							</Grid>
						))}
				</Grid>

			</ExpandSelector>
		);
	}

	renderStatus() {
		const params = this.getParams();
		const { models, constants } = this.state;
		return (
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<JobButton
						label="Reload status"
						color="secondary"
						icon={<ReplayIcon />}
						fn={() => this.loadStatus()}
					/>
				</Grid>
				{!params.length
					? (
						<Grid item xs={12}>
							<SimpleAlert content="Optimization not started" />
						</Grid>
					) : params.map((param) => (
						<Grid item xs={12} key={param.id}>
							{this.renderParam(param)}
						</Grid>
					))}
				<Grid item xs={12}>
					<ExpandSelector title="All models">
						<Grid container spacing={3}>
							<Grid item xs={12}>
								{this.renderModelList(models.BoosterModel)}
							</Grid>
						</Grid>
					</ExpandSelector>
				</Grid>
				<Grid item xs={12}>
					<ExpandSelector title="Debug optimization call" autoExpandKey="debug" noUnmountOnExit>
						<CallDebugger models={models} constants={constants} />
					</ExpandSelector>
				</Grid>
			</Grid>
		);
	}

	render() {
		return (
			<OperationWrapper
				fn={() => this.loadStatus()}
				content={() => this.renderStatus()}
			/>
		);
	}
}

BoosterStatus.propTypes = {
	paramFilter: PropTypes.any,
};

BoosterStatus.defaultProps = {
	paramFilter: {},
};

export default BoosterStatus;
